// The sectioned page (redesign spec §4-5): back link, title with badges and
// actions, then the section nav, the section cards and — when the page area
// is wide enough — the live preview as a third column. Narrower, the preview
// waits behind a "Preview" button in a side sheet. Width is the page area's
// own (measured), not the window's, so collapsing the sidebar opens the
// column. A preview-first page (the template pages) gives up the nav before
// the preview: nav · cards · preview when wide, cards · preview at mid width,
// then cards alone with the sheet. Ported from the vouchers admin's
// EntityDetailShell.

import { Button, Popover, Sheet } from "@pej/kore/components";
import { ChevronLeft, Eye, Info, X } from "lucide-react";
import { cloneElement, type ReactElement, type ReactNode, useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { useElementWidth } from "./use-element-width";

export const PREVIEW_COLUMN_MIN_WIDTH = 1120;

/** A preview-first page keeps the preview as a column down to this page
 *  width: cards ≥ 344px + 32px gap + the 320px preview (template-page spec §2).
 *  Not 712 (cards ≥ 360): `<main>` reserves a scrollbar gutter, and a classic
 *  15–17px scrollbar would push a 1024px window's 720px page area below that. */
export const PREVIEW_FIRST_MIN_WIDTH = 696;

/** Which of the optional columns the page area holds. By default the nav
 *  stays and the preview moves to a sheet first; a preview-first page (the
 *  template pages, where watching the preview is the point) drops the nav
 *  first. Unmeasured (`null`, and always under happy-dom) counts as narrow. */
export function shellColumns({
    width,
    hasNav,
    hasPreview,
    previewFirst,
}: {
    width: number | null;
    hasNav: boolean;
    hasPreview: boolean;
    previewFirst: boolean;
}): { nav: boolean; previewColumn: boolean } {
    const roomForAll = hasNav && hasPreview && width !== null && width >= PREVIEW_COLUMN_MIN_WIDTH;
    if (!previewFirst || !hasPreview) return { nav: hasNav, previewColumn: roomForAll };
    return {
        nav: hasNav && roomForAll,
        previewColumn:
            width !== null && width >= (hasNav && roomForAll ? PREVIEW_COLUMN_MIN_WIDTH : PREVIEW_FIRST_MIN_WIDTH),
    };
}

const BACK_LINK_CLASS =
    "inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-kore-text-muted hover:bg-kore-surface-sunken hover:text-kore-text";

/** The back link: both parts or neither — a top-level page (Security) has none. */
type BackProps =
    | {
          backLabel: string;
          /** A rendered router Link (keeps the route typed at the call site). */
          backLink: ReactElement<{ className?: string; children?: ReactNode }>;
      }
    | { backLabel?: never; backLink?: never };

export type SectionPageShellProps = BackProps & {
    title: string;
    info?: { id: string; createdAt: string };
    badges?: ReactNode;
    actions?: ReactNode;
    /** Omitted on a one-section page: no nav column is reserved. */
    nav?: ReactNode;
    /** The live preview; a third column when there's room, else a sheet behind a "Preview" button. */
    preview?: ReactNode;
    /** Fired with a section id when a nav entry is clicked. */
    onNavClick?: (sectionId: string) => void;
    /** Drop the nav before the preview when space runs out (spec §2). */
    previewFirst?: boolean;
    children: ReactNode;
};

export function SectionPageShell({
    backLabel,
    backLink,
    title,
    info,
    badges,
    actions,
    nav,
    preview,
    onNavClick,
    previewFirst = false,
    children,
}: SectionPageShellProps) {
    const { ref, width } = useElementWidth<HTMLDivElement>();
    const { nav: showNav, previewColumn: wide } = shellColumns({
        width,
        hasNav: nav !== undefined,
        hasPreview: preview !== undefined,
        previewFirst,
    });
    const [sheetOpen, setSheetOpen] = useState(false);
    const [compactVisible, titleRef] = useTitleScrolledAway();

    const previewButton =
        preview !== undefined && !wide ? (
            <Button type="button" variant="outline" size="sm" onClick={() => setSheetOpen(true)}>
                <Eye aria-hidden="true" />
                Preview
            </Button>
        ) : null;
    const allActions = (
        <>
            {previewButton}
            {actions}
        </>
    );
    const back =
        backLink && backLabel
            ? cloneElement(backLink, { className: BACK_LINK_CLASS }, <ChevronLeft className="size-3.5" />, backLabel)
            : null;

    const titleBlock = (
        <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
                <h1 ref={titleRef} className="flex items-center gap-2 text-lg font-semibold tracking-tight">
                    {info && <InfoPopover title={title} info={info} />}
                    {title}
                </h1>
                <div className="ml-auto flex gap-2">{allActions}</div>
            </div>
            {badges && (
                <div data-testid="page-badges" className="flex flex-wrap items-center gap-2">
                    {badges}
                </div>
            )}
        </div>
    );

    return (
        <div ref={ref} className="flex flex-col gap-6">
            <CompactHeader visible={compactVisible} back={back} title={title} actions={allActions} />
            {back && <div>{back}</div>}
            {showNav ? (
                <>
                    <div className="grid grid-cols-[180px_minmax(0,1fr)] gap-8">
                        <div aria-hidden="true" />
                        {titleBlock}
                    </div>
                    <div
                        className={cn(
                            "grid gap-8",
                            wide ? "grid-cols-[180px_minmax(0,1fr)_320px]" : "grid-cols-[180px_minmax(0,1fr)]",
                        )}
                    >
                        {/* The nav and the preview stick 72px below <main>'s top edge
                        (its 24px padding + top-12), clear of the 49px compact header.
                        Sections land on the same line when a nav link scrolls to
                        them: every section carries scroll-mt-[4.5rem]. */}
                        {/* biome-ignore lint/a11y/useKeyWithClickEvents: anchors fire click on Enter themselves; delegation only, the anchors inside are the interactive elements */}
                        <aside
                            className="self-start lg:sticky lg:top-12"
                            onClick={(event) => {
                                const anchor = (event.target as Element).closest("a[href^='#']");
                                if (anchor) onNavClick?.(anchor.getAttribute("href")?.slice(1) ?? "");
                            }}
                        >
                            {nav}
                        </aside>
                        <div className="flex min-w-0 flex-col gap-6 pb-24">{children}</div>
                        {wide && <div className="self-start lg:sticky lg:top-12">{preview}</div>}
                    </div>
                </>
            ) : wide ? (
                <>
                    {titleBlock}
                    <div className="grid grid-cols-[minmax(0,1fr)_320px] gap-8">
                        <div className="flex min-w-0 flex-col gap-6 pb-24">{children}</div>
                        <div className="sticky top-12 self-start">{preview}</div>
                    </div>
                </>
            ) : (
                <>
                    {titleBlock}
                    <div className="flex min-w-0 flex-col gap-6 pb-24">{children}</div>
                </>
            )}
            {preview !== undefined && !wide && (
                <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                    <Sheet.Content side="right" className="w-[420px] max-w-full">
                        <Sheet.Header>
                            {/* kore's Sheet has no close button of its own; Escape
                                and an outside click work, but nothing says so. */}
                            <div className="flex items-center justify-between gap-2">
                                <Sheet.Title>Preview</Sheet.Title>
                                <Sheet.Close
                                    render={
                                        <Button type="button" variant="ghost" size="icon" aria-label="Close preview">
                                            <X aria-hidden="true" />
                                        </Button>
                                    }
                                />
                            </div>
                        </Sheet.Header>
                        <Sheet.Body>{preview}</Sheet.Body>
                    </Sheet.Content>
                </Sheet>
            )}
        </div>
    );
}

/** Whether the page title has scrolled out of `<main>` (the scroll
 *  container), and the ref to put on the title. A callback ref, not a ref
 *  read once: each layout renders the title block in its own column, so a
 *  layout switch (the first width measurement, a resize) replaces the <h1>,
 *  and watching the old, detached one would report it scrolled away. */
function useTitleScrolledAway(): [boolean, (node: HTMLHeadingElement | null) => void] {
    const [title, setTitle] = useState<HTMLHeadingElement | null>(null);
    const [away, setAway] = useState(false);
    useEffect(() => {
        if (!title || typeof IntersectionObserver === "undefined") return;
        // The scroll container is the app shell's <main>, outside this
        // component: found from the title rather than by a global lookup.
        const observer = new IntersectionObserver(([entry]) => entry && setAway(!entry.isIntersecting), {
            root: title.closest("main"),
            threshold: 0,
        });
        observer.observe(title);
        return () => observer.disconnect();
    }, [title]);
    return [away, setTitle];
}

function CompactHeader({
    visible,
    back,
    title,
    actions,
}: {
    visible: boolean;
    back: ReactNode;
    title: string;
    actions: ReactNode;
}) {
    // Sticky at the top of <main>, zero height in the flow so nothing below
    // moves when it appears; the bar itself hangs from it. It repeats the back
    // link and the actions, so while hidden it must leave the accessibility
    // tree too: `inert` keeps its links out of the tab order, `aria-hidden`
    // keeps screen readers (and testing-library) from finding each control
    // twice. -mb-6 cancels the shell's gap-6 after it, so the first row
    // (back link or title) starts at <main>'s padding, level with an index
    // page's title.
    return (
        <div className="sticky top-0 z-30 -mb-6 h-0">
            <div
                data-compact-header="true"
                inert={!visible}
                aria-hidden={!visible}
                className={cn(
                    // -inset-x-6/-top-6 bleed to <main>'s edges (its p-6); px-6
                    // puts the contents back on the page's content edges, so the
                    // repeated actions sit exactly where the title row's were.
                    // Slides down from under the app header: Tailwind 4's
                    // translate-* sets `translate`, so that's what transitions.
                    "absolute -inset-x-6 -top-6 flex items-center gap-3 border-b border-kore-border bg-kore-surface/85 px-6 py-2 backdrop-blur-md transition-[opacity,translate] duration-200 ease-out",
                    visible ? "translate-y-0 opacity-100" : "pointer-events-none -translate-y-full opacity-0",
                )}
            >
                {back && (
                    <>
                        {back}
                        <span aria-hidden="true" className="text-kore-text-muted">
                            /
                        </span>
                    </>
                )}
                <span className="truncate text-sm font-semibold">{title}</span>
                <div className="ml-auto flex items-center gap-2">{actions}</div>
            </div>
        </div>
    );
}

function InfoPopover({ title, info }: { title: string; info: { id: string; createdAt: string } }) {
    return (
        <Popover>
            <Popover.Trigger
                render={
                    <button
                        type="button"
                        aria-label={`${title} info`}
                        className="inline-flex size-5 items-center justify-center rounded-sm text-kore-text-muted hover:text-kore-text"
                    >
                        <Info className="size-3.5" aria-hidden="true" />
                    </button>
                }
            />
            <Popover.Content align="start" className="w-fit max-w-sm text-sm">
                <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                    <dt className="text-kore-text-muted">Id</dt>
                    <dd className="font-mono text-xs">{info.id}</dd>
                    <dt className="text-kore-text-muted">Created</dt>
                    <dd>{new Date(info.createdAt).toLocaleString()}</dd>
                </dl>
            </Popover.Content>
        </Popover>
    );
}
